// src/api-client.ts
import axios from "axios";

// src/url-builder.ts
var buildUrl = (path, options = {}) => {
  if (path.startsWith("/api/")) {
    return path.replace("/api", "");
  }
  return path.startsWith("/") ? path : `/${path}`;
};

// src/api-client.ts
var ApiClient = class {
  constructor(config) {
    this.config = config;
    this.instance = axios.create({
      baseURL: config.baseURL,
      headers: {
        "Accept": "application/json"
      }
    });
    this.instance.interceptors.request.use((config2) => {
      if (this.config.debug) {
        console.log(`[API] Request to ${config2.url} (auth: CAS)`);
      }
      const token = localStorage.getItem("cas_jwt");
      if (token && config2.headers) {
        config2.headers["Authorization"] = `Bearer ${token}`;
        if (this.config.debug) {
          console.log("[API] Added CAS Bearer token");
        }
      }
      const isDebugMode = sessionStorage.getItem("debugModeEnabled") === "true";
      if (isDebugMode && config2.headers) {
        config2.headers["X-Debug-Mode"] = "true";
        if (this.config.debug) {
          console.log("[API] Adding X-Debug-Mode header");
        }
      }
      return config2;
    });
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error)) {
          if (this.config.debug) {
            console.error(`[API] Error ${error.response?.status}: ${error.config?.url}`);
          }
          if (error.response?.status === 401 || error.response?.status === 403) {
            if (this.config.debug) {
              console.error(`[API] Authorization error (${error.response.status})`);
            }
            document.dispatchEvent(new CustomEvent("access-denied"));
          }
        }
        return Promise.reject(error);
      }
    );
  }
  /**
   * Make a request with proper URL construction based on request type
   */
  async request(options) {
    if (options.url) {
      options.url = buildUrl(options.url, options);
    }
    if (this.config.debug) {
      console.log(`[API] ${options.method || "GET"} ${options.url}`);
    }
    return this.instance.request(options);
  }
  /**
   * GET request helper
   */
  async get(url, options = {}) {
    return this.request({ ...options, url, method: "GET" });
  }
  /**
   * POST request helper
   */
  async post(url, data, options = {}) {
    return this.request({ ...options, url, data, method: "POST" });
  }
  /**
   * PUT request helper
   */
  async put(url, data, options = {}) {
    return this.request({ ...options, url, data, method: "PUT" });
  }
  /**
   * DELETE request helper
   */
  async delete(url, options = {}) {
    return this.request({ ...options, url, method: "DELETE" });
  }
  /**
   * Get the underlying axios instance
   */
  getAxiosInstance() {
    return this.instance;
  }
};
function createApiClient(config) {
  return new ApiClient(config);
}

// src/utils.ts
import axios2 from "axios";
function sanitizeResponseData(data) {
  if (!data) return data;
  try {
    const sanitizedData = deepSanitizeFloats(data);
    console.log("\u{1F9F9} Data sanitized successfully");
    return sanitizedData;
  } catch (error) {
    console.warn("\u26A0\uFE0F Data sanitization warning:", error);
    return data;
  }
}
function deepSanitizeFloats(obj) {
  if (obj === null || obj === void 0) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => deepSanitizeFloats(item));
  }
  if (typeof obj === "object") {
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      sanitized[key] = deepSanitizeFloats(value);
    }
    return sanitized;
  }
  if (typeof obj === "string" && /^\d+\.\d+$/.test(obj)) {
    return parseFloat(obj);
  }
  if (typeof obj === "string" && /^\d*\.?\d*$/.test(obj) && obj !== "") {
    const parsed = parseFloat(obj);
    return isNaN(parsed) ? obj : parsed;
  }
  return obj;
}
var handleApiError = (error) => {
  if (!axios2.isAxiosError(error)) {
    throw new Error(error.message || "An unexpected error occurred");
  }
  if (error.response?.status === 404) {
    throw new Error("Resource not found");
  }
  if (error.response?.status === 400) {
    const data = error.response.data;
    throw new Error(data?.detail || "Bad request");
  }
  if (error.response?.status === 500) {
    throw new Error("Server error. Please try again later.");
  }
  throw new Error("Network error. Please check your connection.");
};
export {
  ApiClient,
  buildUrl,
  createApiClient,
  handleApiError,
  sanitizeResponseData
};
//# sourceMappingURL=index.js.map